// useHandActionSequence.js
//
// Generic step-sequence engine for an animated "hero acts -> villain reacts ->
// hero decides" reveal, ahead of the real graded question. Deliberately NOT
// hardcoded to hero-then-villain-then-decide: it just walks an ordered list of
// step objects, so the same hook can later drive longer chains (more actors,
// more reveals) for the bigger simulated-hand feature without a rewrite.
//
// Step shape:
//   { type: 'lockedAction', actor, buttonLabel, resultBet, resultActive }
//     - Predetermined, non-graded action. Waits for the user to tap
//       triggerLockedAction() — nothing happens on its own. Once tapped, it
//       plays out in two paced beats rather than resolving instantly:
//         1. tap -> short hold -> the raise/bet appears
//         2. -> another short hold -> control actually hands to the villain
//       Two beats because the original instant version made "you tapped" and
//       "it's now their turn" indistinguishable — nothing to actually watch
//       unfold.
//   { type: 'reveal', actor, label, resultBet, resultActive }
//     - Villain's (or any non-hero actor's) reaction. Three distinct beats:
//       "thinking" (their turn, bet unchanged) -> "acted" (bet updates,
//       still shown active) -> "returned" (active hands back to hero) ->
//       auto-advances.
//   { type: 'decision', question, buttons }
//     - The real, graded moment. Sequence stops here; today's existing
//       Question/ButtonGroup rendering takes over unchanged.

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

// Each seat's full sequence (tap-or-turn-start through hand-off) totals
// ~1s — the previous, faster split was still hard to actually follow.

// ── Locked-action pacing (hero's own predetermined action, post-tap) ───────
const HERO_BET_DELAY_MS = 500;    // tap -> the raise appears
const HERO_ACTIVE_DELAY_MS = 500; // raise appears -> control hands to villain
const LOCKED_ACTION_PHASES = { WAITING: 0, BET_SHOWN: 1, ACTIVE_MOVED: 2 };

// ── Reveal pacing (villain's automatic reaction) ───────────────────────────
const THINKING_MS = 350;    // "their turn" hold, before their bet changes at all
const ACTED_HOLD_MS = 400;  // bet already updated, still shown active — let it register
const RETURNED_HOLD_MS = 250; // active back on hero, held before auto-advancing
const REVEAL_PHASES = { THINKING: 0, ACTED: 1, RETURNED: 2 };

export function useHandActionSequence(steps, startingBets = { hero: 0, villain: 0 }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [revealPhase, setRevealPhase] = useState(REVEAL_PHASES.THINKING);
  const [lockedActionPhase, setLockedActionPhase] = useState(LOCKED_ACTION_PHASES.WAITING);
  const stepsRef = useRef(steps);
  stepsRef.current = steps;

  const currentStep = steps[stepIndex] || null;

  const advance = useCallback(() => {
    setStepIndex((i) => Math.min(i + 1, stepsRef.current.length - 1));
  }, []);

  // Called by the locked-action button's tap — does nothing by itself, just
  // kicks the WAITING -> BET_SHOWN -> ACTIVE_MOVED timer chain below into
  // motion (via lockedActionPhase, since a plain boolean can't distinguish
  // "tapped, waiting for the first delay" from "already progressed").
  const triggerLockedAction = useCallback(() => {
    setLockedActionPhase((p) => (p === LOCKED_ACTION_PHASES.WAITING ? -1 : p)); // -1 = "tapped, timer pending"
  }, []);

  const reset = useCallback(() => {
    setStepIndex(0);
    setRevealPhase(REVEAL_PHASES.THINKING);
    setLockedActionPhase(LOCKED_ACTION_PHASES.WAITING);
  }, []);

  // Reset both phase machines whenever the step changes.
  useEffect(() => {
    setRevealPhase(REVEAL_PHASES.THINKING);
    setLockedActionPhase(LOCKED_ACTION_PHASES.WAITING);
  }, [stepIndex]);

  // lockedAction: tap (-1) -> after HERO_BET_DELAY_MS -> BET_SHOWN
  useEffect(() => {
    if (!currentStep || currentStep.type !== 'lockedAction' || lockedActionPhase !== -1) return undefined;
    const t = setTimeout(() => setLockedActionPhase(LOCKED_ACTION_PHASES.BET_SHOWN), HERO_BET_DELAY_MS);
    return () => clearTimeout(t);
  }, [lockedActionPhase, currentStep]);

  // lockedAction: BET_SHOWN -> after HERO_ACTIVE_DELAY_MS -> ACTIVE_MOVED
  useEffect(() => {
    if (!currentStep || currentStep.type !== 'lockedAction' || lockedActionPhase !== LOCKED_ACTION_PHASES.BET_SHOWN) return undefined;
    const t = setTimeout(() => setLockedActionPhase(LOCKED_ACTION_PHASES.ACTIVE_MOVED), HERO_ACTIVE_DELAY_MS);
    return () => clearTimeout(t);
  }, [lockedActionPhase, currentStep]);

  // lockedAction: ACTIVE_MOVED -> immediately move on to the next step (the
  // villain's reveal, which starts its own THINKING hold right away — no
  // extra pause needed here, that hold provides the continuity).
  useEffect(() => {
    if (!currentStep || currentStep.type !== 'lockedAction' || lockedActionPhase !== LOCKED_ACTION_PHASES.ACTIVE_MOVED) return undefined;
    advance();
  }, [lockedActionPhase, currentStep, advance]);

  // reveal: THINKING -> ACTED -> RETURNED -> advance (unchanged from before)
  useEffect(() => {
    if (!currentStep || currentStep.type !== 'reveal') return undefined;
    const t1 = setTimeout(() => setRevealPhase(REVEAL_PHASES.ACTED), THINKING_MS);
    return () => clearTimeout(t1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex]);

  useEffect(() => {
    if (!currentStep || currentStep.type !== 'reveal' || revealPhase !== REVEAL_PHASES.ACTED) return undefined;
    const t2 = setTimeout(() => setRevealPhase(REVEAL_PHASES.RETURNED), ACTED_HOLD_MS);
    return () => clearTimeout(t2);
  }, [revealPhase, currentStep]);

  useEffect(() => {
    if (!currentStep || currentStep.type !== 'reveal' || revealPhase !== REVEAL_PHASES.RETURNED) return undefined;
    const t3 = setTimeout(() => advance(), RETURNED_HOLD_MS);
    return () => clearTimeout(t3);
  }, [revealPhase, currentStep, advance]);

  // Resolve hero/villain bet + who's active from every step that has fully
  // completed (strictly before the current index), plus however far the
  // CURRENT step has progressed through its own phases.
  const seatState = useMemo(() => {
    let heroBet = startingBets.hero;
    let villainBet = startingBets.villain;
    let active = 'hero';

    for (let i = 0; i < stepIndex; i++) {
      const step = steps[i];
      if (!step) continue;
      if (step.type === 'lockedAction' || step.type === 'reveal') {
        if (step.actor === 'hero') heroBet = step.resultBet;
        if (step.actor === 'villain') villainBet = step.resultBet;
        active = step.resultActive;
      }
    }

    if (currentStep && currentStep.type === 'lockedAction') {
      if (lockedActionPhase >= LOCKED_ACTION_PHASES.BET_SHOWN) {
        // Bet appears first — active stays wherever it already was.
        if (currentStep.actor === 'hero') heroBet = currentStep.resultBet;
        if (currentStep.actor === 'villain') villainBet = currentStep.resultBet;
      }
      if (lockedActionPhase >= LOCKED_ACTION_PHASES.ACTIVE_MOVED) {
        active = currentStep.resultActive;
      }
    }

    if (currentStep && currentStep.type === 'reveal') {
      if (revealPhase >= REVEAL_PHASES.ACTED) {
        // Bet updates first...
        if (currentStep.actor === 'hero') heroBet = currentStep.resultBet;
        if (currentStep.actor === 'villain') villainBet = currentStep.resultBet;
        active = currentStep.actor; // still active while the bet settles
      }
      if (revealPhase >= REVEAL_PHASES.RETURNED) {
        // ...active hands back afterward, as its own beat.
        active = currentStep.resultActive;
      }
    }

    return { heroBet, villainBet, active };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps, stepIndex, currentStep, revealPhase, lockedActionPhase, startingBets.hero, startingBets.villain]);

  return {
    stepIndex,
    currentStep,
    isRevealing: currentStep?.type === 'reveal' && revealPhase === REVEAL_PHASES.THINKING,
    isDecision: currentStep?.type === 'decision',
    seatState,
    advance,
    triggerLockedAction,
    reset,
  };
}

export default useHandActionSequence;
